import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const ACCENT = "#2563EB";
const ACCENT_DARK = "#1D4ED8";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const SURFACE_ELEVATED = "#0F1420";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#9CA3AF";
const TEXT_DIM = "#6B7280";

const NAV_ITEMS = ["Product", "How It Works", "Pricing"];

/* ── Real product screenshots (user-supplied assets) — used by BrowserFrame
   showcase sections only (capability cards no longer render screenshots). ── */
const IMG_MEDDICC_2  = "https://customer-assets.emergentagent.com/job_0b8b6dd7-614c-440e-a55f-617b3d483267/artifacts/6r9ag63h_The%20MEDDICC%20Tab%202.png";
const IMG_ICP        = "https://customer-assets.emergentagent.com/job_0b8b6dd7-614c-440e-a55f-617b3d483267/artifacts/jag8q635_The%20ICP%20Match%20Tab.png";

/* ── Browser frame with traffic-light dots ── */
const BrowserFrame = ({ src, alt, maxWidth = "100%" }) => (
  <div style={{
    borderRadius: 12, overflow: "hidden",
    background: SURFACE_2, border: `1px solid ${BORDER}`,
    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.65), 0 0 0 1px rgba(37,99,235,0.05)",
    maxWidth,
  }}>
    <div style={{
      height: 30, display: "flex", alignItems: "center", gap: 6,
      padding: "0 14px", background: "#05070C", borderBottom: `1px solid ${BORDER}`,
    }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#EF4444" }} />
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#F59E0B" }} />
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#22C55E" }} />
      <span style={{ marginLeft: "auto", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>inference.app</span>
    </div>
    <img src={src} alt={alt} loading="lazy" style={{ display: "block", width: "100%", height: "auto" }} />
  </div>
);

const Section = ({ children, bg = BG, id, extraClass = "", maxWidth = 1200, pad = "60px 40px" }) => (
  <section id={id} className={extraClass} style={{ background: bg, padding: pad }}>
    <div style={{ maxWidth, margin: "0 auto" }}>{children}</div>
  </section>
);

/* Capability card — icon + title + desc */
const CapabilityCard = ({ icon, title, desc }) => (
  <div className="capability-card" style={{
    background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14,
    padding: 26, display: "flex", flexDirection: "column", gap: 16,
    transition: "border-color 0.25s, transform 0.25s",
  }}>
    <div style={{
      width: 38, height: 38, borderRadius: 9,
      background: `${ACCENT}18`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: ACCENT,
    }}>{icon}</div>
    <h3 style={{ fontSize: 17, fontWeight: 600, color: TEXT_PRIMARY, letterSpacing: "-0.015em" }}>{title}</h3>
    <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.6 }}>{desc}</p>
  </div>
);

const FitCard = ({ color, glow, name, industry, score, label }) => (
  <div style={{
    background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12,
    padding: "24px 26px", display: "flex", flexDirection: "column", gap: 14,
    flex: 1, minWidth: 0,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: color, boxShadow: `0 0 12px ${glow}` }} />
      <span style={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, letterSpacing: "-0.01em" }}>{name}</span>
    </div>
    <span style={{ fontSize: 10.5, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", color: TEXT_DIM, textTransform: "uppercase" }}>{industry}</span>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6 }}>
      <span style={{ fontSize: 44, fontWeight: 700, color, letterSpacing: "-0.03em" }}>{score}</span>
      <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: TEXT_DIM, letterSpacing: "0.04em", textTransform: "uppercase" }}>/ 100</span>
    </div>
    <span style={{
      fontSize: 11, fontWeight: 600, color, background: `${color}14`,
      border: `1px solid ${color}33`, padding: "4px 10px", borderRadius: 6,
      alignSelf: "flex-start", fontFamily: "'DM Mono', monospace",
      letterSpacing: "0.04em", textTransform: "uppercase",
    }}>{label}</span>
  </div>
);

const PersonaCard = ({ icon, title, desc }) => (
  <div style={{
    background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12,
    padding: 26, display: "flex", flexDirection: "column", gap: 14,
  }}>
    <div style={{
      width: 38, height: 38, borderRadius: 9, background: `${ACCENT}18`,
      display: "flex", alignItems: "center", justifyContent: "center", color: ACCENT,
    }}>{icon}</div>
    <h4 style={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, letterSpacing: "-0.01em" }}>{title}</h4>
    <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.65 }}>{desc}</p>
  </div>
);

/* ── Pricing data ── */
const PLANS = [
  {
    name: "Free", monthly: 0, annual: 0,
    persona: "First-time users and individuals evaluating fit",
    freeAlways: true,  // always $0, never affected by billing toggle
    included: [
      "3 analyses in first 7 days",
      "10 analyses in first 30 days",
      "5 analyses per month after trial",
      "Full 11-tab intelligence briefing",
      "ICP profile with fit scoring",
      "No credit card required",
    ],
    excluded: [],
    cta: "Start Free",
  },
];

/* New 4-card pricing structure (Feb 2026 redesign).
   Card 2 (Individual) and Card 4 (Org & Enterprise) each hold a 3-way
   segmented toggle that swaps the displayed sub-tier without reloading. */
const INDIVIDUAL_TABS = [
  {
    id: "individual", label: "Individual", name: "Individual",
    monthly: 79, annual: 63,
    included: [
      "30 analyses per month",
      "Full 11-tab intelligence briefing",
      "MEDDICC auto-populated with discovery questions",
      "Approach Recommendations — Displacement, Greenfield, Augmentation",
      "Platform detection",
      "Contact discovery — top 5 key contacts with LinkedIn",
      "Brief export — PDF, Markdown",
      "ICP profile with fit scoring",
    ],
    excluded: [],
    cta: "Get Individual",
  },
  {
    id: "individual_plus", label: "Plus", name: "Individual Plus",
    monthly: 149, annual: 119,
    subtitle: "Everything in Individual, plus:",
    included: [
      "75 analyses per month",
      "Talking Points card with copy button",
      "Scenario re-runs with delta view",
      "Article summarization",
      "Decision Memory",
      "Copy as Outreach Email",
      "Brief export — PDF, Markdown, JSON",
    ],
    excluded: [],
    cta: "Get Individual Plus",
  },
  {
    id: "individual_pro", label: "Pro", name: "Individual Pro",
    monthly: 299, annual: 239,
    subtitle: "Everything in Individual Plus, plus:",
    included: [
      "150 analyses per month",
      "200 batch analyses per month",
      "5 monitored companies — bi-weekly alerts",
      "Material and minor change detection",
      "5 scenario re-runs per period",
      "Copy Share Link for any analysis",
    ],
    excluded: [],
    cta: "Get Individual Pro",
  },
];

const TEAM_PLAN = {
  name: "Team / Pro", monthly: 799, annual: 639, perSuffix: "/ ws / month",
  persona: "Sales teams, BD teams, and early corp dev",
  included: [
    "250 analyses per workspace",
    "500 batch analyses per month",
    "100 monitored companies — bi-weekly",
    "5 seats included",
    "Team memory and shared summaries",
    "Team activity feed",
    "Shared folders",
    "20 scenario re-runs per period",
  ],
  excluded: [],
  cta: "Get Team",
};

const ORG_TABS = [
  {
    id: "org_standard", label: "Standard", name: "Org Standard",
    priceLabel: "$18,000", perSuffix: "/ year",
    monthlyLabel: "$18,000", monthlyPerSuffix: "/ year",  // unaffected by toggle
    annualOnly: true,
    subtitle: "Everything in Individual, Individual Plus and Individual Pro, plus:",
    persona: "Organizations rolling out across multiple teams",
    included: [
      "500 analyses per workspace",
      "300 batch analyses bundled",
      "1,000 monitored companies — bi-weekly",
      "10 seats included",
      "Up to 3 workspaces",
      "API access",
      "Salesforce integration",
      "Dynamic personas",
    ],
    excluded: [],
    cta: "Get Org Standard",
  },
  {
    id: "org_plus", label: "Plus", name: "Org Plus",
    priceLabel: "$42,000", perSuffix: "/ year",
    monthlyLabel: "$42,000", monthlyPerSuffix: "/ year",  // unaffected by toggle
    annualOnly: true,
    subtitle: "Everything in Individual, Individual Plus and Individual Pro, plus:",
    persona: "Large GTM organizations at scale",
    included: [
      "1,250 analyses per workspace",
      "750 batch analyses bundled",
      "2,500 monitored companies — weekly",
      "25 seats included",
      "Up to 3 workspaces",
      "API access",
      "Salesforce integration",
      "Dynamic personas",
    ],
    excluded: [],
    cta: "Get Org Plus",
  },
  {
    id: "enterprise", label: "Enterprise", name: "Enterprise",
    priceLabel: "Custom", perSuffix: "",
    monthlyLabel: "Custom", monthlyPerSuffix: "",  // unaffected by toggle
    annualOnly: true,
    persona: "Custom contracts for the world's largest organizations",
    included: [
      "Unlimited analyses",
      "Unlimited batch",
      "Unlimited monitoring — daily",
      "Unlimited seats and workspaces",
      "API access",
      "Salesforce integration",
      "Dedicated support",
      "Custom contract and SLA",
    ],
    excluded: [],
    cta: "Contact Sales",
    enterprise: true,
  },
];

/* Segmented 3-way toggle pill used at the top of the Individual and
   Org & Enterprise cards. Active option uses the existing accent colour. */
const SegmentedToggle = ({ options, value, onChange, testIdPrefix }) => (
  <div
    data-testid={`${testIdPrefix}-toggle`}
    style={{
      display: "inline-flex", padding: 2, gap: 2,
      background: BG, border: `1px solid ${BORDER}`, borderRadius: 6,
      marginBottom: 18,
      maxWidth: "100%",
      overflowX: "auto",
    }}
  >
    {options.map(opt => {
      const active = opt.id === value;
      return (
        <button
          key={opt.id}
          data-testid={`${testIdPrefix}-toggle-${opt.id}`}
          data-active={active}
          onClick={() => onChange(opt.id)}
          style={{
            background: active ? ACCENT : "transparent",
            color: active ? "#fff" : TEXT_MUTED,
            border: "none",
            padding: "4px 6px",
            fontSize: 9, fontWeight: 600,
            letterSpacing: "0.02em",
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: "'DM Mono', monospace",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            transition: "background 0.15s, color 0.15s",
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const PriceTile = ({ plan, billing, onCta, topSlot, highlight, badge, testId }) => {
  // Per-tier toggle behavior:
  //   freeAlways → $0 forever, no billing note, no toggle effect
  //   annualOnly → Org Standard / Org Plus / Enterprise — always annual price
  //   otherwise  → respects the page-level monthly/annual toggle
  const isAnnual = plan.freeAlways
    ? false
    : (plan.annualOnly ? true : billing === "annual");

  const orgPriceLabel = isAnnual ? plan.priceLabel : (plan.monthlyLabel || plan.priceLabel);
  const orgPerSuffix = isAnnual ? plan.perSuffix : (plan.monthlyPerSuffix || plan.perSuffix);

  let showPrice;
  let period;
  if (plan.freeAlways) {
    showPrice = "$0";
    period = "";
  } else if (plan.priceLabel) {
    showPrice = orgPriceLabel;
    period = orgPerSuffix;
  } else {
    showPrice = isAnnual && (plan.annual || plan.annual === 0) ? `$${plan.annual}` : `$${plan.monthly}`;
    period = plan.perSuffix || (isAnnual ? "/ month, billed annually" : "/ month");
  }

  // "Save 20%" line only on tiers where the billing toggle actually changes price.
  const showSavingsLine = !plan.freeAlways && !plan.annualOnly && !plan.priceLabel
    && isAnnual && plan.annual !== plan.monthly && plan.monthly > 0;

  // Annual-billed note for Org Standard and Org Plus (but not Enterprise / Free)
  const showAnnualNote = plan.annualOnly && !plan.enterprise && plan.priceLabel !== "Custom";

  const isHero = !!highlight;
  const tileTestId = testId || `pricing-tile-${plan.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <div
      data-testid={tileTestId}
      style={{
        background: isHero ? SURFACE_ELEVATED : SURFACE,
        border: isHero ? `1.5px solid ${ACCENT}` : `1px solid ${BORDER}`,
        borderRadius: 14, padding: 26,
        display: "flex", flexDirection: "column", gap: 14,
        position: "relative",
        height: "100%",  // grid stretches all cards to row height
        boxShadow: isHero ? `0 0 0 1px ${ACCENT}22, 0 20px 60px -30px ${ACCENT}55` : "none",
      }}>
      {badge && (
        <span style={{
          position: "absolute", top: -10, left: 20,
          fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
          color: "#fff", background: ACCENT,
          padding: "4px 10px", borderRadius: 5,
          fontFamily: "'DM Mono', monospace", textTransform: "uppercase",
        }} data-testid="pricing-hero-badge">{badge}</span>
      )}
      {/* Header slot — always rendered with fixed height so the price line
          sits at the same Y on all 4 cards, whether or not this card has a
          segmented toggle (Individual / Org have one; Free / Team do not). */}
      <div style={{ display: "flex", justifyContent: "center", minHeight: 43 }}>
        {topSlot}
      </div>
      <h4 style={{ fontSize: 12.5, fontWeight: 600, color: TEXT_PRIMARY, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'DM Mono', monospace" }}
         data-testid={`${tileTestId}-title`}>{plan.name}</h4>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 30, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em" }}
              data-testid={`${tileTestId}-price`}>{showPrice}</span>
        {period && (
          <span style={{ fontSize: 11.5, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{period}</span>
        )}
      </div>
      {showSavingsLine && (
        <span style={{ fontSize: 11, color: "#22C55E", fontFamily: "'DM Mono', monospace", marginTop: -8 }}>
          Save 20% vs ${plan.monthly}/month
        </span>
      )}
      {showAnnualNote && (
        <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: -8 }}
              data-testid={`${tileTestId}-billed-annually`}>
          billed annually
        </span>
      )}
      {plan.subtitle && (
        <p
          data-testid={`${tileTestId}-subtitle`}
          style={{
            fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5,
            fontStyle: "italic", margin: 0,
          }}
        >
          {plan.subtitle}
        </p>
      )}

      <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        {plan.included.map(line => (
          <div key={line} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 4 }}>
              <path d="M5 13l4 4L19 7" />
            </svg>
            <span style={{ fontSize: 12.5, color: TEXT_PRIMARY, lineHeight: 1.55 }}>{line}</span>
          </div>
        ))}
        {plan.excluded.map(line => (
          <div key={line} style={{ display: "flex", alignItems: "flex-start", gap: 8, opacity: 0.45 }}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 4 }}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span style={{ fontSize: 12.5, color: TEXT_DIM, lineHeight: 1.55, textDecoration: "line-through" }}>{line}</span>
          </div>
        ))}
      </div>

      <button
        className={isHero || plan.enterprise ? "btn-primary" : "btn-ghost"}
        style={{ width: "100%", marginTop: "auto", fontSize: 13, padding: "11px 18px" }}
        onClick={onCta}
        data-testid={`pricing-${plan.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-cta`}
      >{plan.cta}</button>
    </div>
  );
};

export default function InferenceHomepage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [individualTab, setIndividualTab] = useState("individual_pro"); // hero default
  const [orgTab, setOrgTab] = useState("org_standard");                  // org default

  const activeIndividual = INDIVIDUAL_TABS.find(t => t.id === individualTab) || INDIVIDUAL_TABS[2];
  const activeOrg = ORG_TABS.find(t => t.id === orgTab) || ORG_TABS[0];

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const goSignup = () => navigate("/signup");
  const goLogin  = () => navigate("/login");
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileNavOpen(false);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Inter', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::selection { background: ${ACCENT}44; }

        .nav-link { color: ${TEXT_MUTED}; text-decoration: none; font-size: 14px; font-weight: 400; transition: color 0.2s; cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; }
        .nav-link:hover { color: ${TEXT_PRIMARY}; }

        .btn-primary { background: ${ACCENT}; color: #fff; border: none; padding: 10px 22px; border-radius: 7px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: background 0.2s, transform 0.1s; letter-spacing: -0.01em; }
        .btn-primary:hover { background: ${ACCENT_DARK}; transform: translateY(-1px); }

        .btn-ghost { background: transparent; color: ${TEXT_MUTED}; border: 1px solid ${BORDER}; padding: 10px 22px; border-radius: 7px; font-size: 14px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
        .btn-ghost:hover { color: ${TEXT_PRIMARY}; border-color: #374151; }

        .capability-card:hover { border-color: ${ACCENT}55; transform: translateY(-3px); }

        .grid-bg {
          background-image:
            linear-gradient(rgba(37, 99, 235, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(37, 99, 235, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
        }

        .section-label {
          font-size: 11px;
          font-family: 'DM Mono', monospace;
          font-weight: 500;
          letter-spacing: 0.12em;
          color: ${ACCENT};
          text-transform: uppercase;
          display: inline-block;
          padding: 5px 12px;
          background: ${ACCENT}14;
          border: 1px solid ${ACCENT}33;
          border-radius: 5px;
          margin-bottom: 20px;
        }

        .grid-3-cap { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
        .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
        .grid-2x3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
        .feat-split { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
        .feat-split-icp { display: grid; grid-template-columns: 1.3fr 1fr; gap: 60px; align-items: center; }

        .billing-toggle { display: inline-flex; padding: 4px; border-radius: 999px; background: ${SURFACE_2}; border: 1px solid ${BORDER}; }
        .billing-toggle button { background: transparent; border: none; color: ${TEXT_MUTED}; padding: 8px 22px; font-size: 13px; font-weight: 500; cursor: pointer; border-radius: 999px; transition: all 0.2s; font-family: 'DM Sans', sans-serif; }
        .billing-toggle button.active { background: ${ACCENT}; color: #fff; }
        .save-badge { font-size: 10px; font-weight: 700; color: #22C55E; background: #22C55E1A; border: 1px solid #22C55E55; padding: 2px 7px; border-radius: 4px; margin-left: 6px; font-family: 'DM Mono', monospace; letter-spacing: 0.04em; }

        .mobile-menu-btn { display: none; background: transparent; border: none; color: ${TEXT_PRIMARY}; cursor: pointer; padding: 6px; }

        @media (max-width: 900px) {
          .feat-split, .feat-split-icp { grid-template-columns: 1fr !important; gap: 40px !important; }
          .grid-3-cap, .grid-3, .grid-2x3 { grid-template-columns: 1fr !important; }
          .desktop-nav, .desktop-nav-ctas { display: none !important; }
          .mobile-menu-btn { display: inline-flex !important; }
          .marketing-section { padding: 40px 22px !important; }
          .hero-section { padding: 100px 22px 40px !important; }
          .hero-h1 { font-size: 36px !important; line-height: 1.12 !important; }
          .pricing-row { grid-template-columns: 1fr !important; }
          .steps-row { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 901px) and (max-width: 1180px) {
          .grid-3-cap { grid-template-columns: repeat(2, 1fr) !important; }
          .pricing-row { grid-template-columns: repeat(2, 1fr) !important; }
        }

        .fade-in { animation: fadeIn 0.8s ease forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ══════════════════════ NAVIGATION ══════════════════════ */}
      <nav data-mobile-main-nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "0 40px", height: 56,
        display: "flex", alignItems: "center",
        background: scrolled ? "rgba(6,8,15,0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? `1px solid ${BORDER}` : "1px solid transparent",
        transition: "all 0.3s",
      }}>
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <img
            src="/inference-logo.svg"
            alt="Inference"
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            style={{ height: 72, width: "auto", display: "block", cursor: "pointer" }}
          />
        </div>

        <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 28 }}>
          {NAV_ITEMS.map(item => {
            const id = item === "Product" ? "capabilities" : item === "How It Works" ? "how-it-works" : "pricing";
            return <button key={item} className="nav-link" onClick={() => scrollTo(id)} data-testid={`nav-${id}`}>{item}</button>;
          })}
        </div>

        <div className="desktop-nav-ctas" style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, justifyContent: "flex-end" }}>
          <button className="btn-ghost" style={{ padding: "7px 18px", fontSize: 13 }} onClick={goLogin} data-testid="nav-signin-btn">Sign In</button>
          <button className="btn-primary" style={{ padding: "7px 18px", fontSize: 13 }} onClick={goSignup} data-testid="nav-startfree-btn">Start Free</button>
        </div>

        <button className="mobile-menu-btn" style={{ marginLeft: "auto" }} onClick={() => setMobileNavOpen(o => !o)} data-testid="mobile-menu-btn">
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={mobileNavOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
          </svg>
        </button>

        {mobileNavOpen && (
          <div style={{ position: "absolute", top: 56, left: 0, right: 0, background: "rgba(6,8,15,0.98)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${BORDER}`, padding: "18px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
            {NAV_ITEMS.map(item => {
              const id = item === "Product" ? "capabilities" : item === "How It Works" ? "how-it-works" : "pricing";
              return <button key={item} className="nav-link" onClick={() => scrollTo(id)} style={{ textAlign: "left", fontSize: 15 }}>{item}</button>;
            })}
            <button className="btn-ghost" onClick={goLogin}>Sign In</button>
            <button className="btn-primary" onClick={goSignup}>Start Free</button>
          </div>
        )}
      </nav>

      {/* ══════════════════════ 2. HERO (centered, no screenshot) ══════════════════════ */}
      <section className="grid-bg marketing-section hero-section" style={{ padding: "120px 40px 60px", textAlign: "center" }}>
        <div style={{ maxWidth: 880, margin: "0 auto" }} className="fade-in">
          <span className="section-label">Decision Intelligence For Every Meeting</span>
          <h1 className="hero-h1" style={{
            fontSize: 60, fontWeight: 700, letterSpacing: "-0.035em",
            color: TEXT_PRIMARY, lineHeight: 1.04, marginBottom: 26,
          }}>
            Company intelligence. Delivered in seconds.
          </h1>
          <p style={{ fontSize: 18, color: TEXT_MUTED, lineHeight: 1.6, maxWidth: 720, margin: "0 auto 36px" }}>
            Inference pulls live data from across the web, analyzes it through multiple AI perspectives, and returns a complete structured briefing — scored against your exact criteria.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 40, flexWrap: "wrap" }}>
            <button className="btn-primary" style={{ fontSize: 15, padding: "13px 30px" }} onClick={goSignup} data-testid="hero-start-free">Start Free →</button>
            <button className="btn-ghost" style={{ fontSize: 15, padding: "13px 30px" }} onClick={() => scrollTo("how-it-works")} data-testid="hero-how-it-works">See How It Works</button>
          </div>
        </div>
      </section>

      {/* ══════════════════════ 5. WHAT YOU GET — CAPABILITIES (3 cols × 2 rows) ══════════════════════ */}
      <Section id="capabilities" bg={BG} extraClass="marketing-section">
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span className="section-label">Capabilities</span>
          <h2 style={{ fontSize: 42, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.1, marginBottom: 18, maxWidth: 800, margin: "0 auto 18px" }}>
            Everything you need before the conversation starts.
          </h2>
          <p style={{ fontSize: 16, color: TEXT_MUTED, maxWidth: 660, margin: "0 auto", lineHeight: 1.6 }}>
            Every analysis returns a complete structured report — populated with live data and scored against your criteria.
          </p>
        </div>

        <div className="grid-3-cap">
          <CapabilityCard
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16M9 7h1M9 11h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
            title="Complete Company Profile"
            desc="Full company snapshot, payments stack, live fit score, and real-time news. Everything about who they are before you walk in."
          />
          <CapabilityCard
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
            title="Live Signals"
            desc="Funding rounds, leadership changes, product launches, and compliance flags — pulled live from the web the moment you run an analysis."
          />
          <CapabilityCard
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6m-6-3h6m-6-3h6m1 8a7 7 0 10-8 0v3h8v-3z" /></svg>}
            title="Structured Hypotheses"
            desc="Primary buying hypothesis with evidence, alternate scenarios, and explicit unknowns with tailored discovery questions to fill every gap."
          />
          <CapabilityCard
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
            title="MEDDICC Qualification"
            desc="Every MEDDICC dimension populated automatically — confidence scores, evidence citations, and a recommended question for the meeting."
          />
          <CapabilityCard
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><circle cx={12} cy={12} r={9} /><circle cx={12} cy={12} r={5} /><circle cx={12} cy={12} r={1.5} fill="currentColor" /></svg>}
            title="ICP Fit Scoring"
            desc="Ten-dimension scoring matched against your saved ideal customer profile. Green means pursue. Orange means investigate. Red means move on."
          />
          <CapabilityCard
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
            title="Executive Brief"
            desc="A complete one-page executive summary exportable as PDF, Markdown, or JSON — ready before you walk in the room."
          />
        </div>
      </Section>

      {/* ══════════════════════ 6. FIT DOT SYSTEM ══════════════════════ */}
      <Section bg={SURFACE_2} extraClass="marketing-section">
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span className="section-label">Fit Dots</span>
          <h2 style={{ fontSize: 42, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.1 }}>
            Know instantly. Decide faster.
          </h2>
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 40 }}>
          <FitCard color="#22C55E" glow="#22C55E66" name="NovaPay Inc." industry="FinTech" score={88} label="Strong Fit" />
          <FitCard color="#F59E0B" glow="#F59E0B66" name="Meridian Corp." industry="Manufacturing" score={62} label="Worth Exploring" />
          <FitCard color="#EF4444" glow="#EF444466" name="GlobalTech Ltd." industry="Enterprise Software" score={31} label="Poor Fit" />
        </div>
        <p style={{ fontSize: 16, color: TEXT_MUTED, textAlign: "center", maxWidth: 780, margin: "0 auto 18px", lineHeight: 1.65 }}>
          Every company gets a Fit Dot — scored across 10 dimensions against your exact criteria. Green means go. Orange means look closer. Red means move on.
        </p>
        <p style={{ fontSize: 14, color: TEXT_DIM, fontStyle: "italic", textAlign: "center", maxWidth: 680, margin: "0 auto", lineHeight: 1.6 }}>
          When a monitored company changes from Red to Orange, Inference alerts you automatically. That is your signal to re-engage.
        </p>
      </Section>

      {/* ══════════════════════ 7. MEDDICC HIGHLIGHT ══════════════════════ */}
      <Section bg={SURFACE_ELEVATED} extraClass="marketing-section">
        <div className="feat-split">
          <div>
            <span className="section-label">MEDDICC</span>
            <h2 style={{ fontSize: 38, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.12, marginBottom: 22 }}>
              Qualification intelligence — ready before you ask.
            </h2>
            <p style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7, maxWidth: 540 }}>
              Every MEDDICC dimension is automatically populated from live data — each with a confidence score, evidence citations, and a specific recommended question to advance the deal. Walk into every conversation knowing exactly what you know, what you need to find out, and precisely how to ask.
            </p>
          </div>
          <BrowserFrame src={IMG_MEDDICC_2} alt="MEDDICC qualification — Decision Criteria and Process detail" />
        </div>
      </Section>

      {/* ══════════════════════ 8. HOW IT WORKS — 3 steps, no screenshots ══════════════════════ */}
      <Section id="how-it-works" bg={BG} extraClass="marketing-section">
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span className="section-label">How It Works</span>
          <h2 style={{ fontSize: 42, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.1 }}>
            Three steps. 60 seconds.
          </h2>
        </div>
        <div className="steps-row" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 22 }}>
          {[
            { n: "01", t: "Enter the target", d: "Company name, domain, region, and the persona you want to reach. Four fields. That is all." },
            { n: "02", t: "AI swarm analyzes", d: "Multiple AI analyst personas examine live data simultaneously — each with a distinct analytical lens covering risk, growth, operations, buyer mapping, and data quality." },
            { n: "03", t: "Your briefing is ready", d: "Eleven intelligence modules populated with real evidence, scored against your criteria, and exportable in one click." },
          ].map(s => (
            <div key={s.n} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 28, display: "flex", flexDirection: "column", gap: 14 }}>
              <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", fontWeight: 600, color: ACCENT, letterSpacing: "0.08em" }}>STEP {s.n}</span>
              <h3 style={{ fontSize: 19, fontWeight: 600, color: TEXT_PRIMARY, letterSpacing: "-0.015em" }}>{s.t}</h3>
              <p style={{ fontSize: 14, color: TEXT_MUTED, lineHeight: 1.65 }}>{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ══════════════════════ 9. ICP MATCH HIGHLIGHT (large screenshot, left) ══════════════════════ */}
      <Section bg={SURFACE_ELEVATED} extraClass="marketing-section">
        <div className="feat-split-icp">
          <div style={{ minWidth: 0 }}>
            <BrowserFrame src={IMG_ICP} alt="ICP Match — 10-dimension scoring against your saved profile" />
          </div>
          <div>
            <span className="section-label">ICP Match</span>
            <h2 style={{ fontSize: 38, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.12, marginBottom: 22 }}>
              Scored against your exact criteria.
            </h2>
            <p style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7, maxWidth: 520 }}>
              Every analysis is measured across ten dimensions against your saved ideal customer profile — your target industries, buyer personas, revenue thresholds, and deal criteria. Two people analyzing the same company get different intelligence because they are working toward different goals.
            </p>
          </div>
        </div>
      </Section>

      {/* ══════════════════════ 10. WHO IT IS FOR — no pills row ══════════════════════ */}
      <Section bg={BG} extraClass="marketing-section">
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span className="section-label">Who It's For</span>
          <h2 style={{ fontSize: 40, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.1, maxWidth: 820, margin: "0 auto" }}>
            Built for the people who need to know before they go.
          </h2>
        </div>
        <div className="grid-2x3">
          <PersonaCard
            icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
            title="Sales Teams"
            desc="Walk into every meeting prepared. Know who to reach, what pain to surface, and how to qualify the opportunity before the first call."
          />
          <PersonaCard
            icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
            title="Business Development"
            desc="Screen potential partners in minutes. Prioritize the conversations worth having and skip the ones that will not move forward."
          />
          <PersonaCard
            icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            title="Private Equity & VC"
            desc="Run lightweight pre-diligence at scale. Screen hundreds of companies without burning analyst hours on targets that will not qualify."
          />
          <PersonaCard
            icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
            title="Corporate Development & M&A"
            desc="Build and maintain intelligence on acquisition targets over time. Carry institutional memory across deal cycles."
          />
          <PersonaCard
            icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            title="Strategy & Expansion"
            desc="Map new markets and identify target accounts before committing resources. Understand competitive landscapes with structured intelligence."
          />
          <PersonaCard
            icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
            title="Executive Preparation"
            desc="Walk into any board meeting, partnership discussion, or investor conversation fully briefed — in minutes, not hours."
          />
        </div>
      </Section>

      {/* ══════════════════════ 11. PRICING ══════════════════════ */}
      <Section id="pricing" bg={SURFACE_2} extraClass="marketing-section">
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <span className="section-label">Pricing</span>
          <h2 style={{ fontSize: 42, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.1, marginBottom: 18 }}>
            Start free. Scale when you are ready.
          </h2>
          <p style={{ fontSize: 16, color: TEXT_MUTED, maxWidth: 700, margin: "0 auto", lineHeight: 1.65 }}>
            Intelligence quality never degrades across tiers. You pay for scale, monitoring frequency, and team features — never for better AI.
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 44 }}>
          <div className="billing-toggle" data-testid="billing-toggle">
            <button className={billing === "monthly" ? "active" : ""} onClick={() => setBilling("monthly")} data-testid="billing-monthly">Monthly</button>
            <button className={billing === "annual"  ? "active" : ""} onClick={() => setBilling("annual")}  data-testid="billing-annual">
              Annual<span className="save-badge">SAVE 20%</span>
            </button>
          </div>
        </div>

        <div className="pricing-row" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 22, alignItems: "stretch" }}>
          {/* CARD 1 — Free */}
          <PriceTile
            plan={PLANS[0]}
            billing={billing}
            onCta={goSignup}
            testId="card-free"
          />
          {/* CARD 2 — Individual (3-way toggle). Hero highlight applied
              ONLY when Individual Pro is the active sub-tier. */}
          <PriceTile
            plan={activeIndividual}
            billing={billing}
            onCta={goSignup}
            highlight={activeIndividual.id === "individual_pro"}
            badge={activeIndividual.id === "individual_pro" ? "Most Popular" : null}
            testId="card-individual"
            topSlot={
              <SegmentedToggle
                testIdPrefix="individual"
                options={INDIVIDUAL_TABS}
                value={individualTab}
                onChange={setIndividualTab}
              />
            }
          />
          {/* CARD 3 — Team / Pro */}
          <PriceTile
            plan={TEAM_PLAN}
            billing={billing}
            onCta={goSignup}
            testId="card-team"
          />
          {/* CARD 4 — Org & Enterprise (3-way toggle) */}
          <PriceTile
            plan={activeOrg}
            billing={billing}
            onCta={activeOrg.id === "enterprise"
              ? () => window.location.href = "mailto:notifications@inferenceapp.io?subject=Inference%20Enterprise%20Inquiry"
              : goSignup}
            testId="card-org"
            topSlot={
              <SegmentedToggle
                testIdPrefix="org"
                options={ORG_TABS}
                value={orgTab}
                onChange={setOrgTab}
              />
            }
          />
        </div>
        <p style={{ fontSize: 13, color: TEXT_DIM, textAlign: "center", marginTop: 32, lineHeight: 1.6 }}>
          Annual plans save 20%. Org plans are annual only. All tiers include the same intelligence quality.
        </p>
      </Section>

      {/* ══════════════════════ 12. FINAL CTA ══════════════════════ */}
      <Section bg={BG} extraClass="marketing-section" pad="60px 40px">
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: 48, fontWeight: 700, letterSpacing: "-0.035em", color: TEXT_PRIMARY, lineHeight: 1.05, marginBottom: 22 }}>
            Your next meeting starts in 48 hours.
          </h2>
          <p style={{ fontSize: 18, color: TEXT_MUTED, lineHeight: 1.55, marginBottom: 36 }}>
            Walk in knowing more than the person across the table.
          </p>
          <button className="btn-primary" style={{ fontSize: 15, padding: "14px 34px" }} onClick={goSignup} data-testid="final-cta-start-free">
            Start Free — no credit card required
          </button>
          <div style={{ marginTop: 22 }}>
            <a href="mailto:sales@inference.app" style={{ fontSize: 14, color: TEXT_MUTED, textDecoration: "none", borderBottom: `1px solid ${BORDER}` }}>
              Need a team or enterprise plan? Book a demo →
            </a>
          </div>
        </div>
      </Section>

      {/* ══════════════════════ FOOTER ══════════════════════ */}
      <footer style={{ background: SURFACE, borderTop: `1px solid ${BORDER}`, padding: "40px 40px 32px" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", display: "flex", flexWrap: "wrap", gap: 22, justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {NAV_ITEMS.concat(["Sign In", "Start Free"]).map(item => (
              <button key={item} className="nav-link" style={{ fontSize: 12.5 }} onClick={() => {
                if (item === "Sign In") goLogin();
                else if (item === "Start Free") goSignup();
                else scrollTo(item === "Product" ? "capabilities" : item === "How It Works" ? "how-it-works" : "pricing");
              }}>{item}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate("/privacy"); }} className="nav-link" style={{ fontSize: 12.5 }} data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms" onClick={(e) => { e.preventDefault(); navigate("/terms"); }} className="nav-link" style={{ fontSize: 12.5 }} data-testid="footer-terms">Terms of Service</a>
          </div>
        </div>
        <div style={{ maxWidth: 1240, margin: "32px auto 0", paddingTop: 20, borderTop: `1px solid ${BORDER}`, textAlign: "center" }}>
          <span style={{ fontSize: 11.5, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>© 2026 Inference. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
